import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { PlusIcon, SendIcon } from '../components/ui';
import { useAppPath } from '../lib/appBase';
import { formatUsd } from '../lib/format';
import type {
  BalancesChainBalance,
  MyBalancesResponse,
} from '../lib/types';

const STABLE_SYMBOLS = new Set(['USDC', 'USDC.e', 'USDT', 'pUSD']);

export function sumStable(chain: BalancesChainBalance | undefined): number {
  if (!chain) return 0;
  return chain.tokens
    .filter((tok) => STABLE_SYMBOLS.has(tok.symbol))
    .reduce((acc, tok) => acc + Number(tok.amount || 0), 0);
}

export function mainWalletUsd(b: MyBalancesResponse | undefined): number {
  return sumStable(b?.main_wallet.polygon) + sumStable(b?.main_wallet.arbitrum);
}

/** Card surface with an optional title — the cabinet's primary block. */
export function Panel({
  title,
  action,
  className,
  children,
}: {
  title?: ReactNode;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section
      className={clsx(
        'rounded-pb-lg border border-pb-border-soft bg-pb-paper/60 p-5 shadow-pb-sm transition-colors duration-200 hover:border-pb-accent-line/50 sm:p-6',
        className,
      )}
    >
      {title || action ? (
        <div className="mb-4 flex items-center justify-between gap-3">
          {title ? (
            <h2 className="pb-display text-[17px] leading-none text-tg-text">
              {title}
            </h2>
          ) : (
            <span />
          )}
          {action}
        </div>
      ) : null}
      {children}
    </section>
  );
}

function Tile({
  label,
  amount,
  accent,
}: {
  label: string;
  amount: number;
  accent?: boolean;
}) {
  return (
    <div
      className={clsx(
        'flex items-center justify-between gap-3 rounded-pb-md border px-4 py-3',
        accent
          ? 'border-pb-accent-line bg-pb-accent-soft/50'
          : 'border-pb-border-soft bg-pb-paper2/50',
      )}
    >
      <span className="pb-caption">{label}</span>
      <span className="pb-numeral text-[16px] font-semibold text-tg-text">
        {formatUsd(amount)}
      </span>
    </div>
  );
}

export function BalanceTiles({ b }: { b: MyBalancesResponse | undefined }) {
  const { t } = useTranslation();
  const main = mainWalletUsd(b);
  const pm = Number(b?.pm_bot_active.amount || 0);
  const hl = Number(b?.hl_bot_active.amount || 0);
  const total = main + pm + hl;

  return (
    <div className="space-y-4">
      <div className="rounded-pb-lg border border-pb-accent-line/40 bg-pb-accent-soft/40 p-5 sm:p-6">
        <div className="pb-eyebrow">{t('cabinet.total_balance')}</div>
        <div className="pb-numeral mt-1 text-[40px] font-semibold leading-none text-tg-text">
          {formatUsd(total)}
        </div>
      </div>
      <div className="space-y-2.5">
        <Tile label={t('cabinet.tile_main')} amount={main} accent />
        <Tile label={t('cabinet.tile_pm')} amount={pm} />
        <Tile label={t('cabinet.tile_hl')} amount={hl} />
      </div>
    </div>
  );
}

/** Branded empty state — the Kit Pod whale mascot with a message. */
export function CabEmpty({ msg }: { msg: string }) {
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <img
        src="/whale-mascot.webp"
        alt=""
        aria-hidden
        width={96}
        height={64}
        className="kit-bob-soft w-20 opacity-70"
      />
      <p className="pb-caption max-w-[220px]">{msg}</p>
    </div>
  );
}

export function QuickActions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ap = useAppPath();
  const go = (tab: string) => navigate(`${ap('/wallet')}?tab=${tab}`);
  return (
    <div className="grid grid-cols-3 gap-3">
      <ActionButton
        label={t('cabinet.quick_deposit')}
        onClick={() => go('deposit')}
        icon={<PlusIcon size={18} />}
        primary
      />
      <ActionButton
        label={t('cabinet.quick_transfer')}
        onClick={() => go('transfer')}
        icon={<SendIcon size={16} />}
      />
      <ActionButton
        label={t('cabinet.quick_withdraw')}
        onClick={() => go('withdraw')}
        icon={<SendIcon size={16} />}
      />
    </div>
  );
}

function ActionButton({
  label,
  onClick,
  icon,
  primary,
}: {
  label: string;
  onClick: () => void;
  icon: ReactNode;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'pb-focus-ring flex items-center justify-center gap-2 rounded-pb-md px-3 py-3 text-[14px] font-medium transition-colors',
        primary
          ? 'bg-pb-accent text-pb-accent-ink hover:bg-pb-accent-hi'
          : 'border border-pb-border-soft bg-pb-paper2/60 text-tg-text hover:bg-pb-paper2',
      )}
    >
      {icon}
      {label}
    </button>
  );
}
